import React, {
  useState,
} from 'react';
import {
  shallowEqual,
  useSelector,
} from 'react-redux';

import {
  coreSelectors,
} from '@gymcloud/core';

import {
  Modal,
  Button,
} from 'web-components';

import {
  Flex,
  Text,
  H200,
} from 'web-styled';

import BasicInfo from './BasicInfo';
import { countTotal, FormData } from './utils';
import * as S from './styled';

const mapState = state => ({
  clientsMap: coreSelectors.getResourceMap('clients')(state),
  clientOptions: coreSelectors.getSearchOptions('clients')(state),
  productOptions: coreSelectors.getEntitySearchOptions(
    'products',
  )(state),
});

type Props = {
  isOpen: boolean;
  onRequestCreate: (data: any) => void;
  onRequestClose: () => void;
};

const initialValues: FormData = {
  client_id: '',
  items: [
    {
      name: '',
      quantity: 1,
      price: '',
    },
  ],
  description: '',
  custom_name: '',
  custom_email: '',
  days_until_due: 30,
};

const CreateInvoiceModal = ({
  isOpen,
  onRequestCreate,
  onRequestClose,
}: Props) => {
  const [step, setStep] = useState<string>('basic');
  const [isLoading, setLoading] = useState<boolean>(false);
  const [basicInfo, setBasicInfo] = useState<FormData>(initialValues);
  const {
    clientsMap,
    clientOptions,
    productOptions,
  } = useSelector(mapState, shallowEqual);

  const handleClose = () => {
    setBasicInfo(initialValues);
    setStep('basic');

    onRequestClose();
  };

  const handleCreate = async () => {
    setLoading(true);

    try {
      const {
        client_id,
        custom_name,
        custom_email,
        ...rest
      } = basicInfo;
      const data = custom_email ? {
        custom_name,
        custom_email,
        ...rest,
      } : {
        client_id,
        ...rest,
      };

      await onRequestCreate(data);
    } finally {
      setLoading(false);
    }

    handleClose();
  };

  const client = clientsMap[basicInfo.client_id] || {};

  const body: {[k: string]: any} = {
    basic: (
      <BasicInfo
        initialValues={basicInfo}
        clientOptions={clientOptions}
        productOptions={productOptions}
        onCancel={handleClose}
        onSubmit={(values, actions) => {
          setBasicInfo(values);
          actions.setSubmitting(false);
          setStep('review');
        }}
      />
    ),
    review: (
      <Flex
        column
        spacing={20}
        width="100%"
      >
        <H200>Review</H200>
        <Flex
          column
          spacing={10}
          width="100%"
        >
          <Flex
            column
            width="100%"
          >
            <S.Item
              padding="10px 0"
            >
              <Text size="16px" weight="bold">
                {basicInfo.custom_email
                  ? `${basicInfo.custom_name}, ${basicInfo.custom_email}`
                  : coreSelectors.getFullName(client)}
              </Text>
              <Text color="link">
                {client.email}
              </Text>
            </S.Item>

            {basicInfo.items.map((item, index) => (
              <S.Item
                key={index.toString()}
                padding="15px 0"
              >
                <Flex spacing={5}>
                  <Text>
                    {item.name}
                  </Text>
                  <Text color="#ccc">
                    {`x ${item.quantity}`}
                  </Text>
                </Flex>
                <Text>
                  {'$ '}
                  {item.price}
                </Text>
              </S.Item>
            ))}
          </Flex>
          <Flex
            width="100%"
            align="center"
            justify="flex-end"
            spacing={15}
          >
            <Text>
              Total
            </Text>
            <Text weight="bold">
              {'$ '}
              {countTotal(basicInfo.items)}
            </Text>
          </Flex>
        </Flex>
        <Flex column spacing={10}>
          <Text size="16px" weight="bold">
            Memo
          </Text>
          {basicInfo.description ? (
            <Text whiteSpace="pre-line">
              {basicInfo.description}
            </Text>
          ) : (
            <Text color="#ccc">
              No memo
            </Text>
          )}
        </Flex>
        <hr />
        <Flex align="center" spacing={5}>
          <Text>
            Payment is due after
          </Text>
          <Text weight="bold">
            {basicInfo.days_until_due}
          </Text>
          <Text>
            days
          </Text>
        </Flex>

        <Flex
          width="100%"
          justify="space-between"
          margin="30px 0 0 0"
        >
          <Button
            ghost
            width="140px"
            spacing="large"
            appearance="primary"
            isDisabled={isLoading}
            onClick={() => setStep('basic')}
          >
            Back
          </Button>

          <Button
            width="140px"
            spacing="large"
            appearance="success"
            onClick={handleCreate}
            isDisabled={isLoading}
            isLoading={isLoading}
          >
            Send Invoice
          </Button>
        </Flex>
      </Flex>
    ),
  };

  return (
    <Modal
      title="Create Invoice"
      contentStyle={{
        width: '700px',
      }}
      isOpen={isOpen}
      onRequestClose={handleClose}
    >
      {body[step]}
    </Modal>
  );
};

export default CreateInvoiceModal;
